import type { Product, Promo } from "../manifest/schema.js";

/** A shop promo the basket can apply. */
export interface ShopPromo extends Promo {
  shopId: string;
  shopName: string;
}

export type ApplyCodeResult = { ok: true; promo: ShopPromo; message: string } | { ok: false; message: string };

export interface BasketLine {
  productId: string;
  colour: string;
  size: string;
  qty: number;
  /** Extra line of detail (e.g. a configured car's wheels and trim). Part of the line's identity. */
  detail?: string;
  /** Media path (with extension) of a picture of exactly what was configured. */
  image?: string;
}

export interface Storage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const FREE_DELIVERY_PENCE = 50_000;
export const DELIVERY_PENCE = 2_500;
const KEY = "red.basket.v1";
const PROMO_KEY = "red.basket.promo.v1";

/** Codes are typed loosely: trim, drop inner spaces, upper-case. */
export function normaliseCode(raw: string): string {
  return raw.replace(/\s+/g, "").toUpperCase();
}

export function formatGbp(pence: number): string {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: pence % 100 === 0 ? 0 : 2 }).format(pence / 100);
}

export const PRICE_ON_REQUEST = "Price on request";

export interface TotalsText {
  subtotal: string;
  /** Discount row, e.g. "−£29.25"; null when no code takes anything off. */
  discount: string | null;
  /** e.g. "TERRACE15 · Terrace telescope offer (15% off McLaren)". */
  discountLabel: string | null;
  delivery: string;
  total: string;
  /** Something in the basket has no price. */
  hasPriceOnRequest: boolean;
  /** Nothing in the basket has a price: it is an enquiry, not an order. */
  enquiryOnly: boolean;
}

/** Display text for the totals. Price-on-request lines never add to a total and never trigger delivery. */
export function totalsText(b: Basket): TotalsText {
  const poa = b.priceOnRequestCount > 0;
  const enquiryOnly = poa && b.subtotalPence === 0;
  const withPoa = (amount: string): string => (enquiryOnly ? PRICE_ON_REQUEST : poa ? `${amount} + price on request` : amount);
  return {
    subtotal: withPoa(formatGbp(b.subtotalPence)),
    discount: b.discountPence > 0 ? `−${formatGbp(b.discountPence)}` : null,
    discountLabel: b.promo && b.discountPence > 0 ? `${b.promo.code} · ${b.promo.label} (${b.promo.percentOff}% off ${b.promo.shopName})` : null,
    delivery: enquiryOnly ? "To be confirmed" : b.deliveryPence === 0 ? "Free" : formatGbp(b.deliveryPence),
    total: withPoa(formatGbp(b.totalPence)),
    hasPriceOnRequest: poa,
    enquiryOnly
  };
}

/** Demo basket. Pure state + optional persistence. No payments, ever. */
export class Basket {
  private _lines: BasketLine[] = [];
  private _promo: ShopPromo | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly products: ReadonlyMap<string, Product>,
    private readonly storage?: Storage,
    private readonly promos: readonly ShopPromo[] = []
  ) {
    this.restore();
  }

  /** The applied code, if any (it stays applied while the basket changes; it only takes money off eligible lines). */
  get promo(): ShopPromo | null {
    return this._promo;
  }

  /** Priced units of the promo's shop, the only lines a code discounts. */
  private eligiblePence(promo: ShopPromo): number {
    return this._lines.reduce((sum, l) => {
      const p = this.products.get(l.productId);
      return p && p.shopId === promo.shopId && p.pricePence !== undefined ? sum + p.pricePence * l.qty : sum;
    }, 0);
  }

  get discountPence(): number {
    return this._promo ? Math.round((this.eligiblePence(this._promo) * this._promo.percentOff) / 100) : 0;
  }

  /** Apply a discount code. Unknown codes, and codes with nothing to take off, are rejected and change nothing. */
  applyCode(raw: string): ApplyCodeResult {
    const code = normaliseCode(raw);
    if (!code) return { ok: false, message: "Enter a code first." };
    const promo = this.promos.find((p) => p.code === code);
    if (!promo) return { ok: false, message: `Sorry, ${code.slice(0, 20)} is not a valid code.` };
    if (this.eligiblePence(promo) === 0) return { ok: false, message: `${promo.code} only applies to priced ${promo.shopName} items. Add one to your basket first.` };
    this._promo = promo;
    this.changed();
    return { ok: true, promo, message: `${promo.code} applied: ${promo.percentOff}% off ${promo.shopName} items, ${formatGbp(this.discountPence)} off.` };
  }

  removeCode(): void {
    if (!this._promo) return;
    this._promo = null;
    this.changed();
  }

  get lines(): readonly BasketLine[] {
    return this._lines;
  }

  get count(): number {
    return this._lines.reduce((n, l) => n + l.qty, 0);
  }

  get subtotalPence(): number {
    return this._lines.reduce((sum, l) => sum + (this.products.get(l.productId)?.pricePence ?? 0) * l.qty, 0);
  }

  /** Units whose product has no price ("price on request"). They never add to the totals. */
  get priceOnRequestCount(): number {
    return this._lines.reduce((n, l) => n + (this.products.get(l.productId)?.pricePence === undefined ? l.qty : 0), 0);
  }

  /** Delivery only applies when something with a price is in the basket. */
  get deliveryPence(): number {
    const goods = this.subtotalPence - this.discountPence;
    return this.subtotalPence === 0 || goods >= FREE_DELIVERY_PENCE ? 0 : DELIVERY_PENCE;
  }

  get totalPence(): number {
    return this.subtotalPence - this.discountPence + this.deliveryPence;
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  add(productId: string, colour?: string, size?: string, qty = 1, extra: { detail?: string; image?: string } = {}): void {
    const p = this.products.get(productId);
    if (!p || qty < 1) return;
    const line: BasketLine = { productId, colour: colour ?? p.colours[0]?.name ?? "", size: size ?? p.sizes[0] ?? "", qty, ...(extra.detail ? { detail: extra.detail } : {}), ...(extra.image ? { image: extra.image } : {}) };
    const existing = this._lines.find((l) => l.productId === line.productId && l.colour === line.colour && l.size === line.size && (l.detail ?? "") === (line.detail ?? ""));
    if (existing) existing.qty += qty;
    else this._lines.push(line);
    this.changed();
  }

  setQty(index: number, qty: number): void {
    const line = this._lines[index];
    if (!line) return;
    if (qty < 1) this._lines.splice(index, 1);
    else line.qty = Math.min(qty, 10);
    this.changed();
  }

  remove(index: number): void {
    this.setQty(index, 0);
  }

  clear(): void {
    this._lines = [];
    this._promo = null;
    this.changed();
  }

  private changed(): void {
    this.storage?.setItem(KEY, JSON.stringify(this._lines));
    this.storage?.setItem(PROMO_KEY, this._promo?.code ?? "");
    for (const fn of this.listeners) fn();
  }

  private restore(): void {
    const code = this.storage?.getItem(PROMO_KEY);
    this._promo = (code && this.promos.find((p) => p.code === code)) || null;
    try {
      const raw = this.storage?.getItem(KEY);
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      this._lines = parsed
        .filter((l): l is BasketLine => typeof l === "object" && l !== null && typeof l.productId === "string" && this.products.has(l.productId) && typeof l.qty === "number" && l.qty > 0)
        .map((l) => ({
          productId: l.productId,
          colour: String(l.colour ?? ""),
          size: String(l.size ?? ""),
          qty: l.qty,
          ...(typeof l.detail === "string" ? { detail: l.detail } : {}),
          // Stored media paths must stay plain relative paths (no scheme, no `..`).
          ...(typeof l.image === "string" && /^[a-z0-9][a-z0-9/_.-]*$/i.test(l.image) && !l.image.includes("..") ? { image: l.image } : {})
        }));
    } catch {
      this._lines = [];
    }
  }
}
